import { db } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import type { EPCR, Incident, VitalSet, Procedure, MedicationGiven, Narrative } from '@/types';
import type { NEMSISExportData } from '@moquintrend/nemsis';

/**
 * Assembles all ePCR data into a NEMSISExportData payload,
 * generates the XML, validates it, and queues for RIEMSIS submission.
 */
export async function prepareNEMSISExport(epcrId: string): Promise<{
  xml: string;
  validation: { valid: boolean; errors: { element: string; message: string }[]; warnings: { element: string; message: string }[] };
}> {
  // Gather all related records from IndexedDB
  const epcr = await db.epcrs.get(epcrId);
  if (!epcr) throw new Error('ePCR not found');

  const incident = await db.incidents.get(epcr.incident_id);
  if (!incident) throw new Error('Incident not found');

  const vitals = await db.vital_sets.where('epcr_id').equals(epcrId).sortBy('timestamp');
  const procedures = await db.procedures.where('epcr_id').equals(epcrId).sortBy('timestamp');
  const medications = await db.medications_given.where('epcr_id').equals(epcrId).sortBy('timestamp');
  const narratives = await db.narratives.where('epcr_id').equals(epcrId).toArray();
  const narrative = narratives[0] ?? null;

  // Foundation §2.17 — pull the newest hospital-outcome feedback row
  // (if any) so the eOutcome section exports the current snapshot.
  // Offline-safe: a failed fetch simply omits the eOutcome block.
  let outcome: import('./outcome-feedback-service').OutcomeFeedbackRow | null = null;
  try {
    const { latestOutcomeFeedback } = await import('./outcome-feedback-service');
    outcome = await latestOutcomeFeedback(epcrId);
  } catch { /* offline or table missing — export without it */ }

  const exportData = mapToNEMSIS(incident, epcr, vitals, procedures, medications, narrative, outcome);

  const { generateNEMSISXml, validateNEMSISXml } = await import('@moquintrend/nemsis');

  const xml = generateNEMSISXml(exportData);
  const validation = validateNEMSISXml(xml);

  return { xml, validation };
}

/**
 * Submit validated XML to RIEMSIS.
 * In production, this posts to the RIDOH endpoint.
 * In dev, it logs and marks the report as submitted.
 */
export async function submitToRIEMSIS(epcrId: string, xml: string): Promise<{
  success: boolean;
  message: string;
  submittedAt: string;
}> {
  const endpoint = import.meta.env.VITE_RIEMSIS_ENDPOINT;
  const now = new Date().toISOString();

  if (!endpoint) {
    // Dev mode — simulate success. console.log is dev-only (Finding 79);
    // production builds where VITE_RIEMSIS_ENDPOINT is set never hit
    // this branch anyway.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[RIEMSIS Dev] Would submit XML:', xml.substring(0, 200) + '...');
    }

    // Update sync status
    const epcr = await db.epcrs.get(epcrId);
    if (epcr) {
      const incident = await db.incidents.get(epcr.incident_id);
      if (incident) {
        await db.incidents.update(incident.id, {
          sync_status: 'nemsis_sent',
          _dirty: true,
          _last_modified: now,
        });
        await enqueueSync('incidents', incident.id, 'update', {
          id: incident.id,
          sync_status: 'nemsis_sent',
          updated_at: now,
        });
      }
    }

    return { success: true, message: 'Dev mode — simulated RIEMSIS submission', submittedAt: now };
  }

  // Production submission
  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/xml',
        // Auth headers would be added here from agency RIEMSIS credentials
      },
      body: xml,
    });

    if (response.ok) {
      // Update sync status
      const epcr = await db.epcrs.get(epcrId);
      if (epcr) {
        const incident = await db.incidents.get(epcr.incident_id);
        if (incident) {
          await db.incidents.update(incident.id, {
            sync_status: 'nemsis_sent',
            _dirty: true,
            _last_modified: now,
          });
          await enqueueSync('incidents', incident.id, 'update', {
            id: incident.id,
            sync_status: 'nemsis_sent',
            updated_at: now,
          });
        }
      }

      return { success: true, message: 'RIEMSIS submission accepted', submittedAt: now };
    }

    const errorText = await response.text();
    return { success: false, message: `RIEMSIS rejected: ${response.status} — ${errorText}`, submittedAt: now };
  } catch (err) {
    return {
      success: false,
      message: `RIEMSIS submission failed: ${err instanceof Error ? err.message : 'Network error'}`,
      submittedAt: now,
    };
  }
}

/**
 * Check all locked ePCRs approaching the 2hr RIEMSIS window.
 * Returns incidents that need attention.
 */
export async function checkTwoHourCompliance(): Promise<{
  atRisk: { incidentId: string; runNumber: string; minutesRemaining: number }[];
  overdue: { incidentId: string; runNumber: string; minutesOverdue: number }[];
}> {
  const twoHoursMs = 2 * 60 * 60 * 1000;
  const warningThresholdMs = 30 * 60 * 1000; // Warn at 30 min remaining
  const now = Date.now();

  const incidents = await db.incidents
    .where('sync_status')
    .anyOf(['draft', 'synced', 'nemsis_pending'])
    .toArray();

  const completedIncidents = incidents.filter((i) => i.status === 'complete' || i.status === 'locked');

  const atRisk: { incidentId: string; runNumber: string; minutesRemaining: number }[] = [];
  const overdue: { incidentId: string; runNumber: string; minutesOverdue: number }[] = [];

  for (const incident of completedIncidents) {
    const completedAt = new Date(incident.available_time ?? incident.updated_at).getTime();
    const deadline = completedAt + twoHoursMs;
    const remaining = deadline - now;

    if (remaining < 0) {
      overdue.push({
        incidentId: incident.id,
        runNumber: incident.run_number,
        minutesOverdue: Math.round(Math.abs(remaining) / 60000),
      });
    } else if (remaining < warningThresholdMs) {
      atRisk.push({
        incidentId: incident.id,
        runNumber: incident.run_number,
        minutesRemaining: Math.round(remaining / 60000),
      });
    }
  }

  return { atRisk, overdue };
}

function mapToNEMSIS(
  incident: Incident,
  epcr: EPCR,
  vitals: VitalSet[],
  procedures: Procedure[],
  medications: MedicationGiven[],
  narrative: Narrative | null,
  outcome: import('./outcome-feedback-service').OutcomeFeedbackRow | null = null,
): NEMSISExportData {
  return {
    agencyId: incident.agency_id,
    softwareCreator: 'EMSPCR',
    softwareName: 'EMSPCR.app',
    softwareVersion: '0.1.0',
    incidentNumber: incident.run_number,
    responseNumber: incident.run_number,
    dispatchTime: incident.dispatch_time,
    enRouteTime: incident.en_route_time,
    onSceneTime: incident.on_scene_time,
    transportTime: incident.transport_time,
    atDestinationTime: incident.at_hospital_time,
    backInServiceTime: incident.available_time,
    patient: {
      lastName: epcr.patient_last_name,
      firstName: epcr.patient_first_name,
      middleName: null,
      dob: epcr.patient_dob,
      gender: epcr.patient_gender,
      race: epcr.patient_race,
      address: epcr.patient_address,
      city: null,
      state: null,
      zip: null,
      ssn: null, // Never export SSN in standard flow
    },
    situation: {
      dispatchReason: epcr.dispatch_reason,
      chiefComplaint: epcr.chief_complaint,
      primaryImpression: epcr.primary_impression,
      secondaryImpressions: epcr.secondary_impressions,
      sceneSafetyFlags: epcr.scene_safety_flags,
    },
    assessments: epcr.assessments.map((a) => ({
      system: a.system,
      findings: a.findings,
    })),
    vitals: vitals.map((v) => ({
      timestamp: v.timestamp,
      source: v.source,
      bpSystolic: v.bp_systolic,
      bpDiastolic: v.bp_diastolic,
      heartRate: v.heart_rate,
      heartRhythm: v.heart_rhythm,
      respiratoryRate: v.respiratory_rate,
      spo2: v.spo2,
      etco2: v.etco2,
      gcsEye: v.gcs_eye,
      gcsVerbal: v.gcs_verbal,
      gcsMotor: v.gcs_motor,
      gcsTotal: v.gcs_total,
      temperature: v.temperature,
      bloodGlucose: v.blood_glucose,
      painScale: v.pain_scale,
    })),
    procedures: procedures.map((p) => ({
      timestamp: p.timestamp,
      procedureType: p.procedure_type,
      bodySite: p.body_site,
      attemptNumber: p.attempt_number,
      outcome: p.outcome,
      complications: p.complications,
      performedBy: p.performed_by,
    })),
    medications: medications.map((m) => ({
      timestamp: m.timestamp,
      drugName: m.drug_name,
      dose: m.dose,
      unit: m.unit,
      route: m.route,
      patientResponse: m.patient_response,
      performedBy: m.performed_by,
    })),
    disposition: {
      transportDecision: epcr.transport_decision,
      destinationHospital: epcr.destination_hospital,
      hospitalChoiceReason: epcr.hospital_choice_reason,
      // Four-dimension split (§2.16). Values flow from DispositionTab
      // when that refactor lands; null-safe today so exports stay
      // valid while new fields are being captured.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      unitDisposition: (epcr as any).unit_disposition ?? null,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      patientEvaluationCare: (epcr as any).patient_evaluation_care ?? null,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      crewDisposition: (epcr as any).crew_disposition ?? null,
      destinationState: null,
      destinationCounty: null,
      destinationZip: null,
      typeOfDestination: null,
      hospitalCapability: null,
      preArrivalActivation: null,
      preArrivalAlertTime: null,
      transportMethod: null,
      transportMode: null,
      transportModeDescriptors: [],
      finalAcuity: null,
      levelOfCareProvided: null,
    },

    // Foundation §2.3 / §2.4 / §2.7 / §2.2 — structured sections
    dispatch: {
      complaintReported: epcr.dispatch_reason,
      emdPerformed: null,
    },
    scene: {
      firstEMSUnit: epcr.unit_id ?? null,
      numPatients: null,
      mci: null,
      mciTriageClass: null,
      locationType: null,
      state: incident.state,
      zip: incident.zip,
      county: null,
    },
    responseDelays: {
      dispatch: epcr.eresponse_dispatch_delays ?? [],
      response: epcr.eresponse_response_delays ?? [],
      scene: epcr.eresponse_scene_delays ?? [],
      transport: epcr.eresponse_transport_delays ?? [],
      turnaround: epcr.eresponse_turnaround_delays ?? [],
      unitNumber: epcr.unit_id ?? null,
      callSign: epcr.unit_id ?? null,
      responseMode: null,
    },

    // Foundation §2.9 Trauma Set (conditional)
    injury: {
      possibleInjury: epcr.possible_injury
        ? (epcr.possible_injury === 'yes' ? 'Yes' : epcr.possible_injury === 'no' ? 'No' : 'Unknown')
        : null,
      causes: epcr.injury_causes ?? [],
      triageRed: epcr.trauma_triage_red ?? [],
      triageYellow: epcr.trauma_triage_yellow ?? [],
    },

    // Foundation §2.11 History — eHistory.17 drives RIDOH + CMS too
    history: {
      barriersToCare: [],
      alcoholDrugIndicators: epcr.alcohol_drug_indicators ?? [],
    },

    // Foundation §2.10 Cardiac Arrest (conditional). Populated when
    // Code99Mode state is active. Today surfaces the impression-level
    // trigger; full cross-walk from the Code99 event log lands with
    // the eArrest mapper pass.
    arrest: /cardiac arrest|pulseless|rosc/i.test(epcr.primary_impression ?? '')
      ? {
          cardiacArrest: 'Yes',
          etiology: null,
          resuscitationAttempted: [],
          witnessedBy: [],
          aedPriorEMS: null,
          cprType: [],
          firstRhythm: null,
          rosc: /rosc/i.test(epcr.primary_impression ?? '') ? ['Yes'] : [],
          arrestTime: null,
          reasonCprDiscontinued: null,
          rhythmAtDestination: [],
          endOfArrest: null,
          firstCPRBy: null,
          firstAEDBy: null,
          firstDefibBy: null,
        }
      : undefined,

    // Foundation §2.13 Protocols Used — surfaces protocol_state
    protocolsUsed: (epcr.protocol_state?.active_protocol_ids ?? []),

    // Foundation §2.17 Outcome — populated from the eOutcome
    // feedback buffer (migration 019). The newest row per ePCR wins;
    // older rows remain as the audit trail.
    outcome: outcome
      ? {
          edDisposition: outcome.ed_disposition,
          hospitalDisposition: outcome.hospital_disposition,
          edProcedures: outcome.ed_procedures,
          hospitalProcedures: outcome.hospital_procedures,
          hospitalDiagnosis: outcome.hospital_diagnoses ?? [],
          edAdmissionTime: outcome.ed_admission_time,
          hospitalDischargeTime: outcome.hospital_discharge_time,
        }
      : undefined,

    // Foundation §11.4 RIDOH opioid surveillance
    ridohOpioidSurveillance: !!epcr.ridoh_opioid_surveillance,

    narrative: narrative?.edited_text ?? narrative?.generated_text ?? null,
  };
}
